import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, DollarSign, Users, Package, ShoppingCart, Activity, FileText, PieChart, ArrowUpDown } from 'lucide-react';
import { PeriodFilter } from '../components/PeriodFilter';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format numbers with comma as thousand separator
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ReportsPage = () => {
  const [salesReport, setSalesReport] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('income');
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });

  useEffect(() => {
    fetchReports();
  }, [periodFilter]);

  const fetchReports = async () => {
    try {
      const dateParams = new URLSearchParams();
      if (periodFilter.startDate) dateParams.append('start_date', periodFilter.startDate);
      if (periodFilter.endDate) dateParams.append('end_date', periodFilter.endDate);
      const qs = dateParams.toString() ? `?${dateParams.toString()}` : '';
      
      const [sales, income, balance, cash] = await Promise.all([
        axios.get(`${API_URL}/reports/sales${qs}`),
        axios.get(`${API_URL}/reports/income-statement${qs}`),
        axios.get(`${API_URL}/reports/balance-sheet`),
        axios.get(`${API_URL}/reports/cash-flow${qs}`)
      ]);
      setSalesReport(sales.data);
      setIncomeStatement(income.data);
      setBalanceSheet(balance.data);
      setCashFlow(cash.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">Reports & Analytics</h1>
        <p className="text-muted-foreground">View business performance and financial reports</p>
      </div>

      {/* Period Filter */}
      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* Sales Report */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Sales Report</h2>
            <p className="text-sm text-muted-foreground">Revenue and order statistics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
            </div>
            <p className="text-3xl font-heading font-bold text-foreground">{salesReport?.total_orders || 0}</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
            </div>
            <p className="text-3xl font-heading font-bold text-foreground">RWF {formatNumber(salesReport?.total_revenue)}</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Sales by Customer</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sales-by-customer-table">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Total Orders</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Total Revenue</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Avg Order Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(!salesReport?.by_customer || salesReport.by_customer.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No sales data available</td>
                  </tr>
                )}
                {salesReport?.by_customer?.map((item, index) => (
                  <tr key={index} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{item.customer_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{item.total_orders}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-foreground">RWF {formatNumber(item.total_revenue)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      RWF {formatNumber(item.total_revenue / item.total_orders)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Financial Reports with Tabs */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Financial Reports</h2>
            <p className="text-sm text-muted-foreground">Income statement, balance sheet, and cash flow</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('income')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'income' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="income-statement-tab"
          >
            Income Statement
          </button>
          <button
            onClick={() => setActiveTab('balance')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'balance' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="balance-sheet-tab"
          >
            Balance Sheet
          </button>
          <button
            onClick={() => setActiveTab('cashflow')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'cashflow' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="cash-flow-tab"
          >
            Cash Flow
          </button>
        </div>

        {/* Tab Content - Income Statement */}
        {activeTab === 'income' && (
          <div data-testid="income-statement-content">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">RWF {formatNumber(incomeStatement?.revenue)}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">RWF {formatNumber(incomeStatement?.total_operating_expenses)}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Net Profit</p>
                <p className={`text-2xl font-bold ${(incomeStatement?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  RWF {formatNumber(incomeStatement?.net_profit)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Cost of Goods Sold</p>
                <p className="text-xl font-bold text-foreground">RWF {formatNumber(incomeStatement?.cost_of_goods_sold)}</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Gross Profit</p>
                <p className="text-xl font-bold text-foreground">RWF {formatNumber(incomeStatement?.gross_profit)}</p>
              </div>
            </div>
            {incomeStatement?.operating_expenses && Object.keys(incomeStatement.operating_expenses).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">Expense Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(incomeStatement.operating_expenses).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                      <span className="text-sm text-muted-foreground capitalize">{category}</span>
                      <span className="text-sm font-medium text-foreground">RWF {formatNumber(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Content - Balance Sheet */}
        {activeTab === 'balance' && (
          <div data-testid="balance-sheet-content">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  Assets
                </h4>
                <div className="space-y-2">
                  {balanceSheet?.assets && Object.entries(balanceSheet.assets).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center p-3 bg-green-50 rounded">
                      <span className="text-sm text-muted-foreground">{category}</span>
                      <span className="text-sm font-medium text-foreground">RWF {formatNumber(amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center p-3 bg-green-100 rounded font-semibold">
                    <span className="text-sm">Total Assets</span>
                    <span className="text-sm text-green-700">RWF {formatNumber(balanceSheet?.total_assets)}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  Liabilities & Equity
                </h4>
                <div className="space-y-2">
                  {/* Liabilities Section */}
                  {balanceSheet?.liabilities && Object.entries(balanceSheet.liabilities).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center p-3 bg-red-50 rounded">
                      <span className="text-sm text-muted-foreground">{category}</span>
                      <span className="text-sm font-medium text-foreground">RWF {formatNumber(amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center p-3 bg-red-100 rounded">
                    <span className="text-sm font-medium">Total Liabilities</span>
                    <span className="text-sm font-medium text-red-700">RWF {formatNumber(balanceSheet?.total_liabilities)}</span>
                  </div>
                  
                  {/* Equity Section */}
                  <div className="mt-4 border-t pt-4">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Owner's Equity</p>
                    {balanceSheet?.equity?.capital !== undefined && balanceSheet?.equity?.capital !== 0 && (
                      <div className="flex justify-between items-center p-2 bg-purple-50 rounded mb-1">
                        <span className="text-sm text-purple-700">Capital</span>
                        <span className="text-sm font-medium text-purple-800">RWF {formatNumber(balanceSheet?.equity?.capital)}</span>
                      </div>
                    )}
                    {balanceSheet?.equity?.retained_earnings !== undefined && (
                      <div className="flex justify-between items-center p-2 bg-indigo-50 rounded mb-1">
                        <span className="text-sm text-indigo-700">Retained Earnings</span>
                        <span className={`text-sm font-medium ${(balanceSheet?.equity?.retained_earnings || 0) >= 0 ? 'text-indigo-800' : 'text-red-700'}`}>
                          RWF {formatNumber(balanceSheet?.equity?.retained_earnings)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center p-3 bg-blue-100 rounded font-semibold">
                      <span className="text-sm">Total Equity</span>
                      <span className={`text-sm ${(balanceSheet?.equity?.total || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        RWF {formatNumber(balanceSheet?.equity?.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Accounting Equation Check */}
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex flex-col md:flex-row justify-center items-center gap-2 text-sm">
                <span className="font-semibold text-green-700">Assets (RWF {formatNumber(balanceSheet?.total_assets)})</span>
                <span className="text-muted-foreground">=</span>
                <span className="font-semibold text-red-700">Liabilities (RWF {formatNumber(balanceSheet?.total_liabilities)})</span>
                <span className="text-muted-foreground">+</span>
                <span className="font-semibold text-blue-700">Equity (RWF {formatNumber(balanceSheet?.equity?.total)})</span>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Cash Flow */}
        {activeTab === 'cashflow' && (
          <div data-testid="cash-flow-content">
            {/* Operating Activities Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Cash Inflows</p>
                <p className="text-2xl font-bold text-green-600">RWF {formatNumber(cashFlow?.operating_activities?.inflows?.total)}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Cash Outflows</p>
                <p className="text-2xl font-bold text-red-600">RWF {formatNumber(cashFlow?.operating_activities?.outflows?.total)}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Net Cash Flow</p>
                <p className={`text-2xl font-bold ${(cashFlow?.net_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  RWF {formatNumber(cashFlow?.net_cash_flow)}
                </p>
              </div>
            </div>

            {/* Detailed Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Cash Inflows */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 text-green-700">Cash Inflows (by Payment Method)</h4>
                <div className="space-y-2">
                  {cashFlow?.operating_activities?.inflows?.by_method && Object.entries(cashFlow.operating_activities.inflows.by_method).map(([method, amount]) => (
                    <div key={method} className="flex justify-between items-center p-3 bg-green-50 rounded">
                      <span className="text-sm text-muted-foreground">{method}</span>
                      <span className="text-sm font-medium text-green-700">RWF {formatNumber(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cash Outflows */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 text-red-700">Cash Outflows</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                    <span className="text-sm text-muted-foreground">Purchases (Inventory)</span>
                    <span className="text-sm font-medium text-red-700">RWF {formatNumber(cashFlow?.operating_activities?.outflows?.purchases)}</span>
                  </div>
                  {cashFlow?.operating_activities?.outflows?.expenses && Object.entries(cashFlow.operating_activities.outflows.expenses).map(([method, amount]) => (
                    <div key={method} className="flex justify-between items-center p-3 bg-orange-50 rounded">
                      <span className="text-sm text-muted-foreground">Expenses ({method})</span>
                      <span className="text-sm font-medium text-orange-700">RWF {formatNumber(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cash Position */}
            {cashFlow?.cash_position?.by_account && Object.keys(cashFlow.cash_position.by_account).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3">Cash Position by Account</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(cashFlow.cash_position.by_account).map(([account, balance]) => (
                    <div key={account} className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground mb-1">{account}</p>
                      <p className={`text-lg font-bold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        RWF {formatNumber(balance)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-foreground">Total Cash Position</span>
                    <span className={`text-2xl font-bold ${(cashFlow?.cash_position?.total_cash || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      RWF {formatNumber(cashFlow?.cash_position?.total_cash)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Transfers Info */}
            {cashFlow?.financing_activities?.transfers && Object.keys(cashFlow.financing_activities.transfers).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3">Internal Transfers (Net Effect)</h4>
                <div className="space-y-2">
                  {Object.entries(cashFlow.financing_activities.transfers).map(([account, amount]) => (
                    <div key={account} className="flex justify-between items-center p-3 bg-purple-50 rounded">
                      <span className="text-sm text-muted-foreground">{account}</span>
                      <span className={`text-sm font-medium ${amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {amount >= 0 ? '+' : ''}RWF {formatNumber(amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;